import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAuthReturnTo, useAuth } from '@repo/common';
import { getJoinInfo } from '../../api/twoDays/join.api';
import { logoutUser } from '../../api/user/user.api';
import { GhostButton, Notice, PrimaryButton } from '../../pages/TwoDays/TwoDaysJoin';
import { JOB_LABEL, initials } from './model/derive';
import { KST, formatDue } from './model/time';
import useTitleBadge from './useTitleBadge';

/**
 * 워크보드 입장 전 화면 — 보드를 열 수 없을 때 Pre-join 첫 화면처럼 팀 링크 정보를 서버에서 확인하고 다음 할 일을 알려준다.
 *   로그인 전       → 로그인 (끝나면 이 보드로 돌아온다)
 *   다른 계정       → 다른 계정으로 로그인 · 운영진 문의 (다른 팀이면 그 팀 보드로)
 *   Pre-join 전     → Pre-join 으로
 *   이탈 처리됨     → 보드를 열 수 없다는 안내 + 운영진이 남긴 사유
 */

const TeamHeader = ({ info, lead }) => (
  <div className='flex flex-col items-center gap-3 text-center'>
    <span className='flex h-[64px] w-[64px] items-center justify-center rounded-[20px] bg-green-deep02 text-[22px] font-medium text-grayscale-gray00 shadow-[0_0_0_6px_rgba(29,195,123,0.14)]'>
      {initials(info.teamName)}
    </span>
    <div className='flex flex-col gap-1'>
      <span className='text-[14px] leading-[140%] text-text-secondary'>{lead}</span>
      <h1 className='text-[24px] font-medium leading-[125%] tracking-[-0.01em] text-text-primary'>
        {info.teamName}
      </h1>
      <span className='text-[14px] leading-[140%] text-text-tertiary'>
        {info.projectName} · 2 days project
      </span>
    </div>
    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[13px] text-text-secondary'>
      {info.me && (
        <span className='flex items-center gap-1.5'>
          <span className='h-2 w-2 rounded-full bg-green-deep02' />
          {JOB_LABEL[info.me.job]}
          {info.me.leader ? ' · Leader' : ''}
        </span>
      )}
      <span className='flex items-center gap-1.5'>
        <span className='h-2 w-2 rounded-full bg-grayscale-gray05' />
        {info.teamSize} people
      </span>
      {info.teamStartAt && (
        <span className='flex items-center gap-1.5'>
          <span className='h-2 w-2 rounded-full bg-grayscale-gray05' />
          Starts {formatDue(new Date(info.teamStartAt), KST)} KST
        </span>
      )}
    </div>
  </div>
);

const Card = ({ children }) => (
  <div className='flex min-h-screen flex-col items-center justify-center bg-bg-twodays p-6'>
    <div className='flex w-full max-w-[460px] flex-col items-center gap-6 rounded-[20px] bg-grayscale-gray00 px-8 py-10 text-center shadow-3'>
      {children}
    </div>
  </div>
);

const Body = ({ children }) => (
  <p className='max-w-[360px] text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
    {children}
  </p>
);

const BoardGate = ({ slug }) => {
  const navigate = useNavigate();
  const { user, isLoggedIn, loading } = useAuth() ?? {};
  useTitleBadge(0);
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading'); // loading · ready · notFound · error
  const boardPath = `/2days/board/${slug}`;

  useEffect(() => {
    if (loading) return;
    setState('loading');
    getJoinInfo(slug)
      .then((i) => {
        setInfo(i);
        setState('ready');
      })
      .catch((e) => setState(e.response?.status === 404 ? 'notFound' : 'error'));
  }, [slug, loading, isLoggedIn]);

  const login = () => {
    saveAuthReturnTo(boardPath);
    navigate('/login', { state: { returnTo: boardPath } });
  };
  // 다른 계정으로 — 로그아웃한 뒤 로그인하면 이 보드로 돌아온다
  const switchAccount = async () => {
    try {
      await logoutUser();
    } finally {
      saveAuthReturnTo(boardPath);
      window.location.assign('/login');
    }
  };

  if (loading || state === 'loading') return <div className='min-h-screen bg-bg-twodays' />;
  if (state === 'notFound') {
    return (
      <Notice title='This link doesn’t work'>
        Open the team link from your selection email, or contact the organizers.
      </Notice>
    );
  }
  if (state === 'error') {
    return (
      <Notice
        title='Something went wrong'
        actions={<PrimaryButton onClick={() => window.location.reload()}>Try again</PrimaryButton>}
      >
        We couldn’t check this workboard.
      </Notice>
    );
  }

  // 로그인 전 — 어떤 팀 보드인지 보여주고 로그인
  if (!isLoggedIn) {
    return (
      <Card>
        <TeamHeader info={info} lead='Workboard for' />
        <Body>
          Only team members can see this board. Log in with the email you applied with. You’ll come
          back here automatically.
        </Body>
        <PrimaryButton full onClick={login}>
          Log in with Koready
        </PrimaryButton>
      </Card>
    );
  }

  // 이 팀에 없는 계정
  if (!info.me) {
    const email = user?.email || user?.socialEmail || 'another account';
    return (
      <Card>
        <TeamHeader info={info} lead='Workboard for' />
        <Body>
          {info.otherTeamName
            ? `You’re on ${info.otherTeamName}, not ${info.teamName}. Open your own team’s board instead.`
            : `You’re logged in as ${email}, which isn’t on this team. Log in with another account, or contact the organizers.`}
        </Body>
        <div className='flex w-full flex-col gap-2'>
          {info.otherTeamName && (
            <PrimaryButton full onClick={() => navigate('/2days')}>
              Go to my boards
            </PrimaryButton>
          )}
          <GhostButton onClick={switchAccount} className='bg-green-deep03/[0.08]'>
            Log in with another account
          </GhostButton>
        </div>
      </Card>
    );
  }

  // 선정됐지만 Pre-join 전
  if (!info.me.joinedAt) {
    return (
      <Card>
        <TeamHeader info={info} lead='You’ve been selected for' />
        <Body>Finish Pre-join before you open the board. It takes about 3 minutes.</Body>
        <PrimaryButton full onClick={() => navigate(`/2days/join/${slug}`)}>
          Go to Pre-join
        </PrimaryButton>
      </Card>
    );
  }

  // 운영진이 이탈 처리 — 보드 대신 차단 안내 (사유 · 시각)
  if (info.me.droppedAt) {
    return (
      <Card>
        <TeamHeader info={info} lead='Workboard for' />
        <div className='flex flex-col gap-2'>
          <h2 className='text-[18px] font-medium leading-[140%] text-text-primary'>
            You can’t open this workboard
          </h2>
          <Body>
            The Koready team marked you as dropped out of {info.teamName} on{' '}
            {formatDue(new Date(info.me.droppedAt), KST)} KST.
          </Body>
        </div>
        {info.me.dropReason && (
          <div className='flex w-full flex-col gap-1 rounded-xl bg-functional-failed01 px-4 py-3 text-left'>
            <span className='text-[12px] font-medium text-functional-failed04'>Reason</span>
            <p className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary [overflow-wrap:anywhere]'>
              {info.me.dropReason}
            </p>
          </div>
        )}
        <Body>
          Think this is a mistake? Contact us at{' '}
          <a
            href='mailto:contact@ko-ready.com'
            className='text-green-deep03 underline underline-offset-2'
          >
            contact@ko-ready.com
          </a>
          .
        </Body>
        <GhostButton onClick={() => navigate('/2days')} className='bg-green-deep03/[0.08]'>
          Go to my boards
        </GhostButton>
      </Card>
    );
  }

  // 합류했는데 보드를 못 불러왔다
  return (
    <Notice
      title='We couldn’t open the board'
      actions={<PrimaryButton onClick={() => window.location.reload()}>Try again</PrimaryButton>}
    >
      You’re on {info.teamName}. Refresh the page, or contact the organizers if this keeps
      happening.
    </Notice>
  );
};

export default BoardGate;
